import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Download, Loader2, FileText, Shield, ClipboardList, BarChart3,
  Users, Calendar, Target, Wrench, CheckCircle, Scale, Bell,
  FolderOpen, Factory, BookOpen, MessageSquare,
  Package, Eye, Sparkles, RotateCcw, Zap, ImagePlus, X
} from 'lucide-react';

const ICON_MAP = {
  'Delay Event': Bell, 'Notice': FileText, 'Claim': Scale,
  'Variation': Wrench, 'Contract': Shield, 'Compliance': CheckCircle,
  'RFI': ClipboardList, 'Site Instruction': Target, 'Shop Drawing': FolderOpen,
  'Correspondence': MessageSquare, 'Procurement': Package, 'Site Diary': Calendar,
  'Report': BarChart3, 'CRM': Users, 'Document': FileText,
  'Task': Zap, 'Dashboard': BarChart3, 'Close': Factory,
  'Dispute': Scale, 'Knowledge': BookOpen, 'Support': MessageSquare,
  'User': Users, 'RBAC': Shield, 'Map': Target, 'Schedule': Package,
};

function getModuleIcon(name) {
  for (const [key, Icon] of Object.entries(ICON_MAP)) {
    if (name.toLowerCase().includes(key.toLowerCase())) return Icon;
  }
  return FileText;
}

const EXAMPLE_PROMPTS = [
  "Add a summer special: Sign up before March and get 2 months free",
  "Remove procurement and shop drawings — client only needs claims",
  "Focus on FIDIC contracts only, remove JBCC and NEC references",
  "Add sign-up bonus: Free onboarding for first 10 users",
  "Target quantity surveyors — emphasise claims and VOs",
];

function stripHtml(str) {
  if (!str) return '';
  return str.replace(/<font[^>]*>/gi, '').replace(/<\/font>/gi, '').replace(/<br\s*\/?>/gi, ' ').replace(/&amp;/g, '&');
}

function renderHighlighted(str) {
  if (!str) return null;
  const clean = str.replace(/&amp;/g, '&').replace(/<br\s*\/?>/gi, ' ');
  const parts = clean.split(/<font color="([^"]*)">(.*?)<\/font>/gi);
  if (parts.length === 1) return <>{clean}</>;
  const result = [];
  for (let i = 0; i < parts.length; i += 3) {
    if (parts[i]) result.push(<span key={`t${i}`}>{parts[i]}</span>);
    if (i + 2 < parts.length) {
      result.push(<span key={`h${i}`} style={{ color: parts[i + 1] }}>{parts[i + 2]}</span>);
    }
  }
  return <>{result}</>;
}

export default function MarketingFlyer() {
  const [content, setContent] = useState(null);
  const [customContent, setCustomContent] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const activeContent = customContent || content;

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/flyer/default-content`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  const handleGenerate = async () => {
    if (!instructions.trim() && !uploadedImage) { toast.error('Enter customisation instructions or upload a screenshot'); return; }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { instructions: instructions.trim() };
      if (uploadedImage) {
        payload.image_base64 = uploadedImage;
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/flyer/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'AI generation failed');
      }
      const data = await res.json();
      setCustomContent(data);
      setUploadedImage(null);
      setImagePreview(null);
      toast.success('Preview updated with your customisations');
    } catch (e) {
      toast.error(e.message || 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setUploadedImage(base64);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = async (isCustom) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      let res;
      if (isCustom && customContent) {
        res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/flyer/download`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: customContent }),
        });
      } else {
        res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/flyer`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
      }
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isCustom ? 'Gantt_Custom_Flyer.pdf' : 'Gantt_Marketing_Flyer.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = () => {
    setCustomContent(null);
    setInstructions('');
    toast.info('Reset to default flyer');
  };

  if (!activeContent) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
    </div>
  );

  const modules = activeContent.modules || [];
  const benefits = activeContent.benefits || [];
  const specials = activeContent.specials || [];
  const stats = activeContent.stats || [];
  const isCustom = !!customContent;

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-white" data-testid="flyer-page-title">Marketing Flyer</h1>
              {isCustom && (
                <Badge className="bg-purple-900/40 text-purple-300 border-purple-700 text-[10px]">
                  AI Customised
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              {isCustom ? 'Preview shows your customised content' : 'Generate and customise professional PDF flyers'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isCustom && (
              <Button onClick={handleReset} variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="reset-flyer-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> Reset
              </Button>
            )}
            <Button
              onClick={() => handleDownload(isCustom)}
              disabled={downloading}
              className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-5"
              data-testid="download-flyer-btn"
            >
              {downloading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading...</>
              ) : (
                <><Download className="h-4 w-4 mr-2" /> Download PDF</>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {/* AI Customisation */}
        <Card className="bg-slate-900 border-slate-800" data-testid="ai-customisation-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-800 bg-gradient-to-r from-purple-900/20 to-slate-900">
              <Sparkles className="h-5 w-5 text-purple-400" />
              <div className="flex-1">
                <h2 className="text-sm font-bold text-white">AI Customisation</h2>
                <p className="text-[10px] text-slate-500">Describe changes — AI rebuilds the preview below</p>
              </div>
              <Badge className="bg-purple-900/30 text-purple-300 border-purple-700 text-[10px]">GPT-5.2</Badge>
            </div>
            <div className="p-5 space-y-3">
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder='e.g. "Change the green callout background to dark navy. Remove Close-Out. Target FIDIC contracts."'
                className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-600 min-h-[80px] resize-none focus:border-purple-500"
                data-testid="flyer-instructions-input"
              />

              {/* Image Upload */}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              {imagePreview ? (
                <div className="relative rounded-lg border border-slate-700 overflow-hidden">
                  <img src={imagePreview} alt="Uploaded screenshot" className="w-full max-h-40 object-contain bg-slate-950" />
                  <button
                    onClick={() => { setUploadedImage(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1"
                    data-testid="remove-uploaded-image"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-slate-900/80 px-3 py-1">
                    <p className="text-[10px] text-slate-400">Screenshot attached — AI will analyse this with your instructions</p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 text-xs text-slate-500 hover:text-purple-400 transition-colors py-1"
                  data-testid="upload-screenshot-btn"
                >
                  <ImagePlus className="h-4 w-4" />
                  Attach a screenshot of the PDF for AI to analyse
                </button>
              )}
              <div className="flex flex-wrap gap-1.5">
                {EXAMPLE_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setInstructions(prev => prev ? `${prev}\n${p}` : p)}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white border border-slate-700/50 transition-colors"
                    data-testid="quick-prompt-btn"
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleGenerate}
                  disabled={generating || (!instructions.trim() && !uploadedImage)}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6"
                  data-testid="generate-preview-btn"
                >
                  {generating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating Preview...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" /> Generate Preview</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── LIVE PREVIEW ── */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-testid="preview-heading">
              {isCustom ? 'Custom Flyer Preview' : 'Default Flyer Preview'}
            </h3>
          </div>

          {/* Page 1: Cover */}
          <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-testid="preview-cover">
            <CardContent className="p-0">
              <div className="relative bg-gradient-to-b from-slate-900 to-slate-950 px-8 py-12 text-center">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(8,145,178,0.06)_0%,transparent_60%)]" />
                <div className="relative z-10">
                  <p className="text-4xl font-black text-white tracking-tighter mb-1">gantt</p>
                  <p className="text-[10px] font-bold text-cyan-500 tracking-[4px] uppercase mb-6">Artificial Construction Cloud</p>
                  <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight mb-3">
                    {renderHighlighted(activeContent.hero_tagline)}
                  </h2>
                  <p className="text-sm text-slate-400 max-w-lg mx-auto leading-relaxed mb-6">
                    {stripHtml(activeContent.hero_description)}
                  </p>
                  {stats.length > 0 && (
                    <div className="flex justify-center gap-8 mb-6">
                      {stats.map((s) => (
                        <div key={s.label} className="text-center">
                          <p className="text-2xl font-black text-cyan-400">{s.value}</p>
                          <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Specials */}
                  {specials.length > 0 && (
                    <div className="space-y-3 mb-6">
                      {specials.map((sp, i) => (
                        <div
                          key={i}
                          className="mx-auto max-w-md rounded-lg px-6 py-3 border"
                          style={{
                            backgroundColor: (sp.bg_color || '#7C3AED') + '20',
                            borderColor: sp.border_color || sp.bg_color || '#A855F7',
                          }}
                        >
                          <p className="text-sm font-bold text-white">{sp.title}</p>
                          {sp.description && <p className="text-xs text-slate-300 mt-1">{sp.description}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                  {activeContent.contracts_line && (
                    <div className="flex justify-center gap-4 text-[10px] text-slate-600">
                      {activeContent.contracts_line.split('|').map((c, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span>|</span>}
                          <span>{c.trim()}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-slate-800 px-6 py-2 flex items-center justify-between bg-slate-900/50 text-xs text-slate-500">
                <span>Page 1 — Cover</span>
                <span>{modules.length} modules  |  {benefits.length} benefits  |  {specials.length} specials</span>
              </div>
            </CardContent>
          </Card>

          {/* Page 2: Modules */}
          {modules.length > 0 && (
            <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-testid="preview-modules">
              <CardContent className="p-6">
                <p className="text-[10px] font-bold text-cyan-500 tracking-widest mb-1">ALL MODULES</p>
                <h3 className="text-lg font-bold text-white mb-1">{renderHighlighted(activeContent.modules_heading)}</h3>
                <p className="text-xs text-slate-400 mb-4">{stripHtml(activeContent.modules_description)}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {modules.map((m, i) => {
                    const Icon = getModuleIcon(m.name);
                    return (
                      <div key={i} className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2 flex items-start gap-2">
                        <div className="w-6 h-6 rounded flex-shrink-0 flex items-center justify-center mt-0.5"
                          style={{ backgroundColor: (m.color || '#0891b2') + '15' }}>
                          <Icon className="h-3.5 w-3.5" style={{ color: m.color || '#0891b2' }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-white leading-tight">{m.name}</p>
                          <p className="text-[9px] text-slate-500 leading-snug mt-0.5 line-clamp-2">{m.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
              <div className="border-t border-slate-800 px-6 py-2 text-xs text-slate-500">Page 2 — {modules.length} Modules</div>
            </Card>
          )}

          {/* Page 3: Benefits */}
          {benefits.length > 0 && (
            <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-testid="preview-benefits">
              <CardContent className="p-6">
                <p className="text-[10px] font-bold text-cyan-500 tracking-widest mb-1">WHY GANTT</p>
                <h3 className="text-lg font-bold text-white mb-1">{renderHighlighted(activeContent.benefits_heading)}</h3>
                <p className="text-xs text-slate-400 mb-4">{stripHtml(activeContent.benefits_description)}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {benefits.map((b, i) => (
                    <div key={i} className="bg-slate-950 border border-slate-800 rounded-md p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: b.color || '#0891b2' }} />
                        <p className="text-xs font-bold text-white">{b.title}</p>
                      </div>
                      <p className="text-[10px] text-slate-400 leading-relaxed">{b.desc}</p>
                    </div>
                  ))}
                </div>
                {activeContent.callout_text && (
                  <div className="mt-4 rounded-lg px-5 py-3 text-center" style={{
                    backgroundColor: activeContent.callout_bg_color || '#0c4a6e',
                    border: `1px solid ${activeContent.callout_border_color || '#0891b2'}`,
                  }}>
                    <p className="text-sm font-bold text-white">{stripHtml(activeContent.callout_text)}</p>
                    {activeContent.callout_sub && <p className="text-xs text-slate-300 mt-1">{stripHtml(activeContent.callout_sub)}</p>}
                  </div>
                )}
              </CardContent>
              <div className="border-t border-slate-800 px-6 py-2 text-xs text-slate-500">Page 3 — {benefits.length} Benefits</div>
            </Card>
          )}

          {/* Page 4: Contact */}
          <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-testid="preview-contact">
            <CardContent className="p-8 text-center">
              <p className="text-3xl font-black text-white tracking-tighter mb-1">gantt</p>
              <p className="text-[9px] font-bold text-cyan-500 tracking-[3px] uppercase mb-6">Artificial Construction Cloud</p>
              <h3 className="text-xl font-bold text-white mb-2">{renderHighlighted(activeContent.cta_heading)}</h3>
              <p className="text-xs text-slate-400 max-w-md mx-auto mb-6">{stripHtml(activeContent.cta_sub)}</p>
              <div className="flex justify-center gap-8 text-sm">
                <div><p className="text-[9px] text-cyan-400 font-bold tracking-wider">WEBSITE</p><p className="text-white">{activeContent.contact_website}</p></div>
                <div><p className="text-[9px] text-cyan-400 font-bold tracking-wider">EMAIL</p><p className="text-white">{activeContent.contact_email}</p></div>
                <div><p className="text-[9px] text-cyan-400 font-bold tracking-wider">LOCATION</p><p className="text-white">{activeContent.contact_location}</p></div>
              </div>
            </CardContent>
            <div className="border-t border-slate-800 px-6 py-2 text-xs text-slate-500">Page 4 — Contact & CTA</div>
          </Card>
        </div>

        {/* Bottom Download */}
        <div className="text-center py-4">
          <Button
            onClick={() => handleDownload(isCustom)}
            disabled={downloading}
            size="lg"
            className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-10"
            data-testid="download-flyer-btn-bottom"
          >
            {downloading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading...</>
            ) : (
              <><Download className="h-4 w-4 mr-2" /> Download {isCustom ? 'Custom' : 'Default'} PDF</>
            )}
          </Button>
          <p className="text-[10px] text-slate-500 mt-2">
            {isCustom ? 'Downloads the AI-customised version shown above' : 'Professional A4 PDF — ready to share via email, WhatsApp, or print'}
          </p>
        </div>
      </div>
    </div>
  );
}
